import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonMenuToggle,
  IonPage,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToggle,
  IonToolbar,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import {
  checkmarkCircle,
  ellipse,
  menu,
  people,
  personAdd,
} from "ionicons/icons";
import Moment from "react-moment";
import { useCommon } from "../../../common/useCommon";
import {
  getCode,
  getEquipmentFileName,
  isAdditionalEquipment,
} from "../../../common/utils";
import { ICart, ICartItem, IPicture } from "../../../models";
import { usePictures } from "../../Pictures/usePictures";
import { useAdmin } from "../useAdmin";
import { getReservationsApi } from "./api/getReservationsApi";
import ContactList from "./ContactList";
import { useContactList } from "./useContactList";

const ON = "var(--ion-color-success-shade)";
const OFF = "var(--ion-color-danger-shade)";

/** Rolling periods counted back from today; 0 = all orders. */
const PERIODS = [
  { months: 0, label: "Svi periodi" },
  { months: 1, label: "Poslednji mesec" },
  { months: 2, label: "Poslednja 2 meseca" },
  { months: 3, label: "Poslednja 3 meseca" },
  { months: 6, label: "Poslednjih 6 meseci" },
  { months: 12, label: "Poslednjih 12 meseci" },
];

interface IReservationRow {
  cart: ICart;
  reserved: ICartItem[];
  /** Reserved lines that pass the search and the in-stock toggle. */
  matched: ICartItem[];
}

interface IMotifRow {
  pictureId: number;
  quantity: number;
  orders: number;
  lastDate?: Date;
}

// Until the staged useCart fix, the shop saved reservation lines with the cart quantity,
// which is always 0 for a reserved (out-of-stock) motif. Such a line still means one piece.
const reservedQuantity = (item: ICartItem) => Math.max(item.quantity || 0, 1);

const imageSrc = (pictureId: number, picture?: IPicture) =>
  `assets/pics/320/${
    isAdditionalEquipment(pictureId)
      ? getEquipmentFileName(pictureId)
      : picture?.code
  }.jpg`;

const StockDot = ({ on }: { on: boolean }) => (
  <IonIcon icon={ellipse} style={{ fontSize: 10, color: on ? ON : OFF }} />
);

const ReservedMotif = ({
  item,
  picture,
  inStock,
  dimmed,
  allPictures,
}: {
  item: ICartItem;
  picture?: IPicture;
  inStock: boolean;
  dimmed: boolean;
  allPictures: IPicture[];
}) => (
  <div
    style={{
      width: 84,
      textAlign: "center",
      opacity: dimmed ? 0.35 : 1,
    }}
  >
    <img
      src={imageSrc(item.picture_id, picture)}
      alt={picture?.title}
      loading="lazy"
      style={{ height: 64, maxWidth: 84, objectFit: "contain" }}
    />
    <div className="b f13" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
      <StockDot on={inStock} />
      {getCode(item.picture_id, allPictures)}
      {reservedQuantity(item) > 1 && <span>×{reservedQuantity(item)}</span>}
    </div>
    <div
      className="f13"
      style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {picture?.title}
    </div>
  </div>
);

const Reservations = () => {
  useCommon();
  const { selectedCart, setSelectedCart } = useAdmin();
  const { allPicturesAdmin, getAllPicturesAdmin } = usePictures();
  const { contacts, isInList, add, addMany, removeByCart, remove, clear } =
    useContactList();

  const [reservations, setReservations] = useState<ICart[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [groupByMotif, setGroupByMotif] = useState(false);
  const [months, setMonths] = useState(0);
  const [showList, setShowList] = useState(false);
  const contentRef = useRef<HTMLIonContentElement>(null);

  const load = async () => {
    setIsLoading(true);
    const data = await getReservationsApi();
    if (data) {
      setReservations(data);
    }
    setIsLoading(false);
  };

  // Ionic keeps visited pages mounted: reload whenever the page is shown again,
  // and after an order opened from here is closed (it may have been edited).
  const isActive = useRef(false);
  const openedOrder = useRef(false);
  useIonViewWillEnter(() => {
    isActive.current = true;
    load();
  });
  useIonViewWillLeave(() => {
    isActive.current = false;
  });
  useEffect(() => {
    if (selectedCart) {
      openedOrder.current = isActive.current;
    } else if (openedOrder.current) {
      openedOrder.current = false;
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCart]);

  useEffect(() => {
    if (allPicturesAdmin.length === 0) getAllPicturesAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const picturesById = useMemo(() => {
    const map: { [id: number]: IPicture } = {};
    allPicturesAdmin.forEach((p) => (map[p.id] = p));
    return map;
  }, [allPicturesAdmin]);

  // Stock is per country: the order's country decides which flag counts.
  const inStock = (item: ICartItem, cart: ICart) => {
    const p = picturesById[item.picture_id];
    return !!p && (cart.country === "BIH" ? p.statusBIH : p.status);
  };

  const term = search.trim().toLowerCase();
  const isFiltered = !!term || inStockOnly || months > 0;

  const rows = useMemo<IReservationRow[]>(() => {
    let since: Date | undefined;
    if (months > 0) {
      since = new Date();
      since.setMonth(since.getMonth() - months);
      since.setHours(0, 0, 0, 0);
    }
    const matches = (item: ICartItem, cart: ICart) => {
      const p = picturesById[item.picture_id];
      const code = getCode(item.picture_id, allPicturesAdmin) || "";
      return (
        (!term ||
          code.toLowerCase().includes(term) ||
          (p?.title || "").toLowerCase().includes(term)) &&
        (!inStockOnly || inStock(item, cart))
      );
    };
    return reservations
      .filter((cart) => !since || (!!cart.date && new Date(cart.date) >= since))
      .map((cart) => {
        const reserved = (cart.cartItems || []).filter((i) => i.isReservation);
        return { cart, reserved, matched: reserved.filter((i) => matches(i, cart)) };
      })
      .filter((r) => r.matched.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, picturesById, term, inStockOnly, months]);

  // Most reserved first: pieces, then number of orders.
  const motifs = useMemo<IMotifRow[]>(() => {
    const byId: { [pictureId: number]: IMotifRow } = {};
    rows.forEach((r) =>
      r.matched.forEach((i) => {
        const m = (byId[i.picture_id] = byId[i.picture_id] || {
          pictureId: i.picture_id,
          quantity: 0,
          orders: 0,
        });
        const date = r.cart.date ? new Date(r.cart.date) : undefined;
        m.quantity += reservedQuantity(i);
        m.orders += 1;
        if (date && (!m.lastDate || date > m.lastDate)) m.lastDate = date;
      })
    );
    return Object.values(byId).sort(
      (a, b) => b.quantity - a.quantity || b.orders - a.orders
    );
  }, [rows]);

  const matchedQuantity = rows.reduce(
    (sum, r) => sum + r.matched.reduce((s, i) => s + reservedQuantity(i), 0),
    0
  );

  const motifCodes = (r: IReservationRow) =>
    r.matched.map((i) => getCode(i.picture_id, allPicturesAdmin));

  const notInList = rows.filter((r) => !isInList(r.cart.id));

  const toggleInList = (e: React.MouseEvent, r: IReservationRow) => {
    e.stopPropagation();
    if (isInList(r.cart.id)) {
      removeByCart(r.cart.id);
    } else {
      add(r.cart, motifCodes(r));
    }
  };

  // From the motif overview to the orders that reserved it (same period and stock filter).
  const showMotifOrders = (pictureId: number) => {
    setSearch(getCode(pictureId, allPicturesAdmin) || "");
    setGroupByMotif(false);
    contentRef.current?.scrollToTop();
  };

  const status = (c: ICart) =>
    c.package_sent ? "PREUZETA" : c.package_sent_SP ? "SLANJE PAKETA" : "NOVA";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="warning">
          <IonButtons slot="start">
            <IonMenuToggle>
              <IonIcon icon={menu} style={{ fontSize: 28 }} />
            </IonMenuToggle>
          </IonButtons>
          <IonTitle>Rezervacije</IonTitle>
          <IonButtons slot="end" style={{ paddingRight: 8 }}>
            <IonButton fill="solid" color="light" onClick={() => setShowList(true)}>
              <IonIcon slot="start" icon={people} />
              Lista
              <IonBadge color="danger" style={{ marginLeft: 6 }}>
                {contacts.length}
              </IonBadge>
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar color="warning">
          <IonSearchbar
            value={search}
            placeholder="Šifra ili naziv motiva"
            debounce={200}
            onIonInput={(e) => setSearch(e.detail.value || "")}
          />
        </IonToolbar>
        <IonToolbar>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "6px 16px",
              padding: "0 12px",
            }}
          >
            <IonSelect
              aria-label="Period"
              interface="popover"
              value={months}
              onIonChange={(e) => setMonths(e.detail.value)}
              style={{ minWidth: 190 }}
            >
              {PERIODS.map((p) => (
                <IonSelectOption key={p.months} value={p.months}>
                  {p.label}
                </IonSelectOption>
              ))}
            </IonSelect>
            <IonToggle
              checked={inStockOnly}
              onIonChange={(e) => setInStockOnly(e.detail.checked)}
              labelPlacement="end"
            >
              Samo motivi na stanju
            </IonToggle>
            <IonToggle
              checked={groupByMotif}
              onIonChange={(e) => setGroupByMotif(e.detail.checked)}
              labelPlacement="end"
            >
              Grupiši po motivima
            </IonToggle>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "6px 12px",
              padding: "4px 12px",
            }}
          >
            <span className="f13" style={{ flex: 1, minWidth: 140 }}>
              {isLoading && reservations.length === 0
                ? "Učitavanje..."
                : groupByMotif
                ? `${motifs.length} motiva · ${matchedQuantity} kom. · ${rows.length} porudžbina`
                : `${rows.length} porudžbina · ${matchedQuantity} kom.`}
            </span>
            {!groupByMotif && isFiltered && notInList.length > 0 && (
              <IonButton
                size="small"
                onClick={() =>
                  addMany(notInList.map((r) => ({ cart: r.cart, motifs: motifCodes(r) })))
                }
              >
                <IonIcon slot="start" icon={personAdd} />
                Dodaj sve prikazane ({notInList.length})
              </IonButton>
            )}
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef}>
        {isLoading && reservations.length === 0 && (
          <div className="center">
            <IonSpinner name="dots" color="warning" style={{ width: 60, height: 60 }} />
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <div className="ion-text-center" style={{ padding: 30, opacity: 0.7 }}>
            Nema rezervacija za zadati filter.
          </div>
        )}

        {groupByMotif && motifs.length > 0 && (
          <IonList>
            {motifs.map((m, index) => {
              const p = picturesById[m.pictureId];
              return (
                <IonItem
                  key={m.pictureId}
                  button
                  detail={false}
                  onClick={() => showMotifOrders(m.pictureId)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      padding: "6px 0",
                    }}
                  >
                    <span className="f13" style={{ width: 24, textAlign: "right", opacity: 0.6 }}>
                      {index + 1}.
                    </span>
                    <img
                      src={imageSrc(m.pictureId, p)}
                      alt={p?.title}
                      loading="lazy"
                      style={{ width: 56, height: 56, objectFit: "contain", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="b f15">{getCode(m.pictureId, allPicturesAdmin)}</div>
                      <div
                        className="f13"
                        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {p?.title}
                      </div>
                      {p && (
                        <div
                          className="f13"
                          style={{ display: "flex", alignItems: "center", gap: 4, opacity: 0.8 }}
                        >
                          SRB <StockDot on={p.status} /> BIH <StockDot on={p.statusBIH} />
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div className="b" style={{ fontSize: 20 }}>
                        {m.quantity} kom.
                      </div>
                      <div className="f13">{m.orders} porudžbina</div>
                      {m.lastDate && (
                        <div className="f13" style={{ opacity: 0.6 }}>
                          poslednja <Moment format="DD.MM.YYYY.">{m.lastDate}</Moment>
                        </div>
                      )}
                    </div>
                  </div>
                </IonItem>
              );
            })}
          </IonList>
        )}

        {!groupByMotif &&
          rows.map((r) => {
            const c = r.cart;
            const inList = isInList(c.id);
            return (
              <IonCard
                key={c.id}
                className="cursorP"
                onClick={() => setSelectedCart(c)}
                style={{ margin: "8px 6px" }}
              >
                <IonCardContent style={{ padding: 10 }}>
                  <div
                    className="f13"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span className="b">#{5000 + c.id}</span>
                    <Moment format="DD.MM.YYYY.">{c.date}</Moment>
                    {c.country === "BIH" && (
                      <img
                        src="assets/flags/BIH.svg"
                        alt="BIH"
                        style={{ width: 24, height: 16 }}
                      />
                    )}
                    <span style={{ flex: 1 }} />
                    <span className="b" style={{ color: "var(--ion-color-success)" }}>
                      {status(c)}
                    </span>
                  </div>
                  <div className="b f15" style={{ marginTop: 4 }}>
                    {c.name}
                    <span className="f13" style={{ fontWeight: "normal", paddingLeft: 6 }}>
                      {c.place}
                    </span>
                  </div>
                  <div className="f13" style={{ wordBreak: "break-all" }}>
                    {c.phone}
                    {c.email && ` · ${c.email}`}
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    {r.reserved.map((item) => (
                      <ReservedMotif
                        key={item.picture_id}
                        item={item}
                        picture={picturesById[item.picture_id]}
                        inStock={inStock(item, c)}
                        dimmed={!r.matched.includes(item)}
                        allPictures={allPicturesAdmin}
                      />
                    ))}
                  </div>

                  <div style={{ textAlign: "right", marginTop: 6 }}>
                    <IonButton
                      size="small"
                      color={inList ? "success" : "primary"}
                      fill={inList ? "solid" : "outline"}
                      onClick={(e) => toggleInList(e, r)}
                    >
                      <IonIcon slot="start" icon={inList ? checkmarkCircle : personAdd} />
                      {inList ? "Na listi" : "Dodaj na listu"}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            );
          })}
        <ContactList
          isOpen={showList}
          close={() => setShowList(false)}
          contacts={contacts}
          remove={remove}
          clear={clear}
        />
      </IonContent>
    </IonPage>
  );
};

export default Reservations;
